import Service, { service } from '@ember/service';
import type MetricsService from 'garaje/services/metrics';

interface PageTrackingSession {
  startTime: number;
  totalActiveTime: number;
  isVisible: boolean;
  context: string;
  scope: string;
  eventName: string;
  handleVisibilityChange: () => void;
  handleBeforeUnload: () => void;
}

export default class PageVisibilityTrackerService extends Service {
  @service declare metrics: MetricsService;

  #activeSessions = new Map<string, PageTrackingSession>();

  startTracking(sessionId: string, context: string, scope: string, eventName = 'Page Time Spent'): void {
    // clean up any existing session with the same ID
    this.stopTracking(sessionId);

    const session: PageTrackingSession = {
      startTime: Date.now(),
      totalActiveTime: 0,
      isVisible: true,
      context,
      scope,
      eventName,
      handleVisibilityChange: () => this.#handleVisibilityChange(sessionId),
      handleBeforeUnload: () => this.#handleBeforeUnload(sessionId),
    };

    // eslint-disable-next-line ember/use-ember-get-and-set
    this.#activeSessions.set(sessionId, session);

    document.addEventListener('visibilitychange', session.handleVisibilityChange);
    window.addEventListener('beforeunload', session.handleBeforeUnload);
  }

  stopTracking(sessionId: string): void {
    // eslint-disable-next-line ember/use-ember-get-and-set
    const session = this.#activeSessions.get(sessionId);
    if (!session) return;

    // calculate final active time
    if (session.isVisible) {
      session.totalActiveTime += Date.now() - session.startTime;
    }

    // track the time spent
    this.metrics.trackEvent(session.eventName, {
      context: session.context,
      scope: session.scope,
      total_active_time_ms: session.totalActiveTime,
      total_active_time_seconds: Math.round(session.totalActiveTime / 1000),
    });

    // clean up
    document.removeEventListener('visibilitychange', session.handleVisibilityChange);
    window.removeEventListener('beforeunload', session.handleBeforeUnload);

    this.#activeSessions.delete(sessionId);
  }

  #handleVisibilityChange(sessionId: string): void {
    // eslint-disable-next-line ember/use-ember-get-and-set
    const session = this.#activeSessions.get(sessionId);
    if (!session) return;

    const now = Date.now();

    if (document.hidden) {
      // page became hidden (stop counting time)
      if (session.isVisible) {
        session.totalActiveTime += now - session.startTime;
        session.isVisible = false;
      }
    } else {
      // page became visible (start counting time)
      if (!session.isVisible) {
        session.startTime = now;
        session.isVisible = true;
      }
    }
  }

  #handleBeforeUnload(sessionId: string): void {
    // eslint-disable-next-line ember/use-ember-get-and-set
    const session = this.#activeSessions.get(sessionId);
    if (!session) return;

    // track time if user closes tab/refreshes while page is visible
    if (session.isVisible) {
      session.totalActiveTime += Date.now() - session.startTime;

      this.metrics.trackEvent(session.eventName, {
        context: session.context,
        scope: session.scope,
        total_active_time_ms: session.totalActiveTime,
        total_active_time_seconds: Math.round(session.totalActiveTime / 1000),
        exit_type: 'beforeunload',
      });
    }
  }
}
